// Phase 29.3 — Retailer Dashboard (opens after Start Call succeeds).
//
// Mobile-first. Fetches /api/retailers/{id}/dashboard as a single aggregate
// call (≤30KB), plus /current-stock lazily when the Stock tab is opened.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Boxes, Flame, Loader2, MapPin, Package, PieChart, PlusCircle, RotateCcw,
  Settings, ShoppingCart, Sparkles, Target, TrendingUp, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { activityFormRequired, savePendingClose, clearPendingClose } from "@/lib/callForms";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import SalesReturnPanel from "@/pages/field/SalesReturnPanel";
import OutletColorChip from "@/components/outlets/OutletColorChip";
import { useOutletColors } from "@/lib/outletColors";

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0,
});

export default function RetailerDashboard() {
  const { retailer_id } = useParams();
  const nav = useNavigate();
  const { user, hasWorkflowPerm } = useAuth();
  // "Capture Outlet Stock" is a per-user switch for field roles; office roles always see the tab.
  const canStock = !["SE", "ASM", "ZSM", "RSM", "SALES_HEAD"].includes(user?.role_code) || hasWorkflowPerm("STOCK_AT_OUTLET");
  const isAdmin = ["SUPER_ADMIN", "MANAGEMENT"].includes(user?.role_code);
  const [tab, setTab] = useState("overview");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  // Drop 7 · Feature 1 + 2 — dialogs.
  // ?close=1 (coming back from the call's activity forms) opens the "close the call" dialog
  const [noOrderOpen, setNoOrderOpen] = useState(
    new URLSearchParams(window.location.search).get("close") === "1");
  const [reopenOpen, setReopenOpen] = useState(false);
  // For Feature 2 — the completed visit id of the current user at this
  // retailer today (parent for reopen). Null = not eligible.
  const [completedVisitToday, setCompletedVisitToday] = useState(null);
  // Outlet colour coding — this outlet's colour label (none when off).
  const { colors: outletColors } = useOutletColors([retailer_id]);

  const visitIdParam = new URLSearchParams(window.location.search).get("visit_id") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/retailers/${retailer_id}/dashboard`);
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [retailer_id]);

  useEffect(() => { load(); }, [load]);

  // Drop 7 · Feature 2 — probe for a completed visit today at this
  // retailer by the current user. Uses the existing /visits list endpoint
  // filtered client-side to keep the change surface small (no new BE).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/visits", {
          params: { retailer_id, status_filter: "completed",
                     page: 1, page_size: 20 }});
        if (cancelled) return;
        const today_iso = new Date(Date.now() + 5.5 * 3600 * 1000)
          .toISOString().slice(0, 10);
        const mine = (data.items || []).find(
          (v) => v.user_id === user?.id && v.visit_date === today_iso);
        setCompletedVisitToday(mine ? mine.id : null);
      } catch { /* silent — feature just won't show */ }
    })();
    return () => { cancelled = true; };
  }, [retailer_id, user?.id, d]);

  if (loading && !d) return (
    <div className="p-8 text-center text-neutral-400">
      <Loader2 className="h-6 w-6 inline animate-spin" />
    </div>
  );
  if (!d) return <div className="p-6 text-sm text-neutral-500">No data.</div>;

  return (
    <div className="p-4 pb-32 space-y-4"
         data-testid="visit-screen-root"
         data-visit-id={new URLSearchParams(window.location.search).get("visit_id") || ""}>
      {/* Phase 29.5.1 · Item 1 · `visit-screen-root` testid + `data-visit-id`
          attribute enable regression coverage on the Start-Call navigation
          flow. Prior bug: VisitStart consumed `r.data` from offlineMutate
          which returns `r.response` — nav silently threw and never landed
          here. */}
      <div data-testid="retailer-dashboard-screen" style={{display: "none"}} />
      {/* Phase 29.4.1 · pb-32 (128px) clears the mobile bottom nav +
          floating Day-End button at 430×900 and 360×740 viewports. */}
      <button
        onClick={() => nav(-1)}
        data-testid="retailer-dashboard-back"
        className="flex items-center gap-1 text-xs text-neutral-500"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <ProfileCard p={d.profile} colorTag={outletColors[retailer_id]} />

      {/* Phase 29.4 · Header CTAs — Secondary Order (all) + Focus SKU curation (admin) */}
      {/* Drop 7 · Feature 1 — added "No Secondary Order" CTA (in-visit only). */}
      {/* Drop 7 · Feature 2 — added "Reopen call" CTA (shown when caller has  */}
      {/*                     a completed visit at this retailer today).      */}
      {/* Hotfix — Place secondary order used to fire with no visit_id at */}
      {/* all whenever this screen was reached without one (e.g. tapping a */}
      {/* "Visited" outlet from Today's Beat), letting an SE book an order */}
      {/* for an already-completed call without ever going through Reopen */}
      {/* (and its mandatory reason). An order needs an active visit to */}
      {/* attach to, so gate the CTA on visitIdParam the same way "No */}
      {/* Secondary Order" already is below. */}
      <div className="flex flex-wrap gap-2">
        {visitIdParam ? (
          <Button
            size="sm"
            onClick={() => nav(`/field/retailer/${retailer_id}/secondary-order`
                                + `?visit_id=${visitIdParam}`)}
            className="tulsi-gradient text-white h-9 rounded-lg"
            data-testid="rd-place-order-cta"
          >
            <ShoppingCart className="h-4 w-4 mr-1.5" />Place secondary order
          </Button>
        ) : (
          <div className="text-xs text-neutral-500 flex items-center gap-1.5 h-9"
               data-testid="rd-place-order-blocked">
            {completedVisitToday
              ? "Reopen this call (with a reason) to place another order today."
              : "Start a call at this outlet to place an order."}
          </div>
        )}
        {visitIdParam && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setNoOrderOpen(true)}
            className="h-9 rounded-lg border-neutral-300"
            data-testid="rd-no-order-cta"
          >
            <XCircle className="h-4 w-4 mr-1.5" />No Secondary Order
          </Button>
        )}
        {completedVisitToday && !visitIdParam && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setReopenOpen(true)}
            className="h-9 rounded-lg border-[#E5197F] text-[#E5197F]"
            data-testid="rd-reopen-cta"
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />Reopen call
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline"
                  onClick={() => nav("/admin/focus-skus")}
                  className="h-9 rounded-lg"
                  data-testid="rd-set-focus-skus-cta">
            <Settings className="h-4 w-4 mr-1.5" />Set focus SKUs
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`grid w-full ${canStock ? "grid-cols-5" : "grid-cols-4"}`}>
          <TabsTrigger value="overview" data-testid="rd-tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="skus" data-testid="rd-tab-skus">
            SKUs
          </TabsTrigger>
          {canStock && (
            <TabsTrigger value="stock" data-testid="rd-tab-stock">
              Stock
            </TabsTrigger>
          )}
          <TabsTrigger value="history" data-testid="rd-tab-history">
            History
          </TabsTrigger>
          <TabsTrigger value="sret" data-testid="sret-tab">
            Returns
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-3 space-y-3">
          <MetricsBlock m={d.metrics} />
          <TrendChart data={d.metrics.monthly_trend} />
          <SchemesBlock schemes={d.active_schemes} />
        </TabsContent>

        <TabsContent value="skus" className="mt-3 space-y-3">
          <BestSellersBlock rows={d.best_sellers} />
          <FocusBlock rows={d.focus_performance} source={d.focus_sku_source} />
          <MissingBlock rows={d.missing_sku_opportunities}
                          days={d.settings?.missing_sku_days} />
          <NewSKUBlock rows={d.new_sku_opportunities} scope={d.new_sku_scope} />
        </TabsContent>

        {canStock && <TabsContent value="stock" className="mt-3 space-y-3">
          <StockPanel retailerId={retailer_id} />
        </TabsContent>}

        <TabsContent value="history" className="mt-3 space-y-3">
          <HistoryPanel retailerId={retailer_id} />
        </TabsContent>

        <TabsContent value="sret" className="mt-3 space-y-3">
          {/* Phase 29.5.1 · Sales Return in-call tab. */}
          <SalesReturnPanel
            retailer_id={retailer_id}
            distributor_id={d.profile?.distributor_id || null}
            visit_id={new URLSearchParams(window.location.search).get("visit_id") || null}
          />
        </TabsContent>
      </Tabs>

      {/* Drop 7 · Feature 1 — No Secondary Order dialog. */}
      {noOrderOpen && (
        <NoOrderDialog
          visit_id={visitIdParam}
          onNeedForms={() => nav(`/field/call-forms/${visitIdParam}`)}
          onClose={() => setNoOrderOpen(false)}
          onDone={() => { setNoOrderOpen(false); nav("/today-beat", { replace: true }); }}
        />
      )}
      {/* Drop 7 · Feature 2 — Reopen call dialog. */}
      {reopenOpen && (
        <ReopenDialog
          retailer_id={retailer_id}
          parent_visit_id={completedVisitToday}
          retailer_gps={{ lat: d.profile?.gps_lat, lng: d.profile?.gps_lng }}
          beat_id={d.profile?.beat_id || null}
          onClose={() => setReopenOpen(false)}
          onDone={(newVisitId) => {
            setReopenOpen(false);
            nav(`/field/retailer/${retailer_id}/dashboard?visit_id=${newVisitId}`,
                { replace: true });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drop 7 · Feature 1 · No Secondary Order dialog.
// Reuses `visit_np_reason` lookup (same as EndVisitDialog); on submit
// calls existing POST /visits/{id}/end with productive+no_order_reason.
// ---------------------------------------------------------------------------
function NoOrderDialog({ visit_id, onClose, onDone, onNeedForms }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/lookups", { params: { category: "visit_np_reason" } })
       .then(({ data }) => setReasons(
         (data.items || []).filter((r) => r.code !== "auto_skipped")));
  }, []);
  const submit = async () => {
    if (!reason) return toast.error("Select a reason");
    if (reason === "other" && !remarks.trim())
      return toast.error("Remarks required for 'other'");
    setBusy(true);
    try {
      const gps = await new Promise((res) => {
        if (!navigator.geolocation) return res({ lat: 0, lng: 0 });
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude,
                        accuracy_m: Math.round(p.coords.accuracy || 0) }),
          () => res({ lat: 0, lng: 0 }),
          { timeout: 4000 });
      });
      // Hotfix — this dialog hardcoded outcome: "productive" no matter what
      // reason the SE picked, even though its reason list IS the
      // visit_np_reason lookup (the same one non-productive closes use
      // elsewhere) — "Outlet closed" / "Owner absent" / "No demand today"
      // are non-productive reasons, not "productive but no order" ones.
      // Every call closed via this shortcut was landing in Today's
      // Activity/reports as productive regardless of the actual reason.
      const body = {
        gps,
        outcome: "non_productive",
        non_productive_reason: reason,
        non_productive_remarks: reason === "other" ? remarks : null,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.()
                            || `rd-no-order-${visit_id}-${Date.now()}`),
      };
      const r = await offlineMutate({
        method: "POST", path: `/visits/${visit_id}/end`, body,
        entity_type: "visits",
      });
      if (!r.immediate) toast.info("Queued offline");
      else toast.success("Visit closed — no order");
      clearPendingClose(visit_id);
      onDone();
    } catch (e) {
      const need = activityFormRequired(e);
      if (need) {
        // remember the reason just chosen: it is not asked again after the form is filled
        savePendingClose(visit_id, {
          outcome: "non_productive", non_productive_reason: reason,
          non_productive_remarks: reason === "other" ? remarks : null,
        });
        toast.info(need.message); onNeedForms?.();
      }
      else toast.error(formatApiError(e));
    }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="rd-no-order-dialog">
        <DialogHeader>
          <DialogTitle>No secondary order</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-neutral-500">
            Capture a reason so this visit is closed correctly on your beat.
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">
              Reason *
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="rd-no-order-reason-select">
                <SelectValue placeholder="Choose reason" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {reason === "other" && (
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-neutral-500">
                Remarks *
              </Label>
              <Textarea rows={2} value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        maxLength={500}
                        data-testid="rd-no-order-remarks" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="rd-no-order-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="tulsi-gradient text-white"
                  data-testid="rd-no-order-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Close visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Drop 7 · Feature 2 · Reopen dialog.
// Calls POST /visits/start with {is_reopen:true, parent_visit_id, ...}.
// Server-side validation is authoritative (see routes_visits.py).
// ---------------------------------------------------------------------------
function ReopenDialog({ retailer_id, parent_visit_id, retailer_gps, beat_id,
                        onClose, onDone }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/lookups", { params: { category: "visit_reopen_reason" } })
       .then(({ data }) => setReasons(data.items || []));
  }, []);
  const submit = async () => {
    if (!reason) return toast.error("Select a reason");
    if (reason === "other" && !note.trim())
      return toast.error("Note required for 'other'");
    setBusy(true);
    try {
      // Hotfix — this used to silently fall back to `retailer_gps.lat || 0`
      // whenever the device's real GPS failed or timed out. `retailer_gps`
      // was never actually populated (the dashboard profile endpoint has no
      // gps_lat/gps_lng field at all), so that fallback was always (0, 0) —
      // "Null Island" — ~8,000km from anywhere in India, guaranteeing a
      // confusing geofence rejection. And even with real coordinates, a
      // fallback to the retailer's OWN location would make the geofence
      // check trivially always pass, defeating the point of it entirely.
      // Correct behavior: if we can't get a real device fix, say so and
      // stop — same honest failure VisitStart.jsx already uses for the
      // normal (non-reopen) call-start flow.
      const gps = await new Promise((res, rej) => {
        if (!navigator.geolocation) return rej(new Error("This device has no GPS support."));
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude,
                        accuracy_m: Math.round(p.coords.accuracy || 0) }),
          (e) => rej(new Error(`Could not get your location (${e.message}). `
            + "Enable location access for this site and try again.")),
          { enableHighAccuracy: true, timeout: 8000 });
      });
      const body = {
        retailer_id, beat_id, gps,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.()
                            || `rd-reopen-${retailer_id}-${Date.now()}`),
        is_reopen: true,
        parent_visit_id,
        reopen_reason_id: reason,
        reopen_note: reason === "other" ? note : null,
      };
      const r = await offlineMutate({
        method: "POST", path: "/visits/start", body,
        entity_type: "visits",
      });
      if (!r.immediate) {
        toast.info("Reopen queued — will start once online");
        onClose();
        return;
      }
      const nv = r.response;
      if (!nv?.id) {
        toast.error("Reopen succeeded but response missing id");
        return;
      }
      toast.success("Visit reopened");
      onDone(nv.id);
    } catch (e) {
      // Hotfix — reopen goes through the same geofence check as a fresh
      // Start Call, but unlike VisitStart.jsx it had no special handling
      // for that error shape, so the raw machine code ("outside_geo_fence")
      // surfaced instead of a readable distance. Same fix as VisitStart.jsx.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 428 && detail?.error === "outside_geo_fence") {
        toast.error(`Outside ${detail.radius_m}m call-start fence (${detail.distance_m}m away). `
          + "Move closer to the outlet to reopen this call.");
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="rd-reopen-dialog">
        <DialogHeader>
          <DialogTitle>Reopen this call?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-neutral-500">
            A new visit will start at this retailer. Original completed
            visit stays intact for audit.
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">
              Reason *
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="rd-reopen-reason-select">
                <SelectValue placeholder="Choose reason" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {reason === "other" && (
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-neutral-500">
                Note *
              </Label>
              <Textarea rows={2} value={note}
                        onChange={(e) => setNote(e.target.value)}
                        maxLength={500}
                        data-testid="rd-reopen-note" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="rd-reopen-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="tulsi-gradient text-white"
                  data-testid="rd-reopen-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Reopen visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileCard({ p, colorTag }) {
  const hasGps = p.gps_lat != null && p.gps_lng != null;
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="rd-profile">
      <CardContent className="p-4 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-neutral-400">
              {p.channel_type || "Retailer"} · {p.code}
            </div>
            <div className="text-lg font-extrabold text-neutral-900 truncate">
              {p.name}
            </div>
            {colorTag && (
              <div className="mt-0.5">
                <OutletColorChip tag={colorTag} testid="rd-color-chip" />
              </div>
            )}
          </div>
          {hasGps && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${p.gps_lat},${p.gps_lng}`}
              target="_blank" rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#6D2C91]/30 text-[#6D2C91] text-xs font-medium"
              data-testid="rd-navigate-cta"
            >
              <MapPin className="h-3.5 w-3.5" /> Navigate
            </a>
          )}
        </div>
        <div className="text-xs text-neutral-600">{p.address || "—"}</div>
        <div className="text-xs text-neutral-500">
          {p.owner_name ? `${p.owner_name} · ` : ""}{p.phone || ""}
        </div>
      </CardContent>
    </Card>
  );
}

function StatTile({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-neutral-200 p-3 bg-white"
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-400">
        {label}
      </div>
      <div className="text-lg font-bold text-neutral-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-neutral-500">{sub}</div>}
    </div>
  );
}

function MetricsBlock({ m }) {
  // Drop 6 · Item 3 — AOV renders in KG to match the 12-mo trend chart.
  // Backend returns `avg_order_qty_kg_90d` (float | null). `null` means
  // "no weight data" (either zero orders or all lines are pcs-only SKUs);
  // show a soft `— kg` fallback in that case so we don't mislead with 0.
  const kg = m.avg_order_qty_kg_90d;
  const aovLabel = kg == null ? "— kg" : `${kg} kg`;
  return (
    <div className="grid grid-cols-3 gap-2">
      <StatTile testid="rd-metric-orders"
        label="Orders 90d" value={m.orders_last_90d ?? 0} />
      <StatTile testid="rd-metric-freq"
        label="Avg gap"
        value={m.avg_days_between_orders_90d != null
                ? `${m.avg_days_between_orders_90d}d` : "—"} />
      <StatTile testid="rd-metric-aov"
        label="Avg order (kg)"
        value={aovLabel} />
    </div>
  );
}

// "Nice" axis ceiling — rounds up to a tidy step so the top gridline reads
// as a clean number (e.g. 11 -> 12, 23 -> 25) instead of the raw max.
function niceAxisMax(value) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 2, 2.5, 5, 10];
  for (const s of steps) {
    const step = s * magnitude;
    if (Math.ceil(value / step) * step >= value) return Math.ceil(value / step) * step;
  }
  return Math.ceil(value / magnitude) * magnitude;
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function TrendChart({ data }) {
  const rows = (data || []);
  const rawMax = Math.max(0, ...rows.map((r) => r.qty_kg || 0));
  const axisMax = niceAxisMax(rawMax);
  const CHART_H = 96; // px — matches h-24 track
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="rd-trend">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <TrendingUp className="h-4 w-4" />12-month sales trend (kg)
        </div>
        {rows.some((r) => (r.returns_kg || 0) > 0) && (
          <div className="text-[11px] text-rose-700" data-testid="rd-trend-returns-note">
            After returns: {rows.filter((r) => r.returns_kg > 0).map((r) => `${r.month} −${r.returns_kg} kg`).join(", ")}
          </div>
        )}
        {rows.length === 0 ? (
          <div className="text-xs text-neutral-400 py-4 text-center">
            No orders in the last 12 months.
          </div>
        ) : (
          <div className="flex gap-2">
            {/* Y-axis: 3 ticks (max / half / 0). */}
            <div className="flex flex-col justify-between text-[9px] text-neutral-400 shrink-0 text-right"
                 style={{ height: CHART_H, width: 24 }}>
              <span>{axisMax}</span>
              <span>{Math.round(axisMax / 2)}</span>
              <span>0</span>
            </div>
            <div className="flex-1 relative border-l border-neutral-200"
                 style={{ height: CHART_H }} data-testid="rd-trend-plot">
              {/* Gridlines at 0% / 50% / 100%. */}
              <div className="absolute left-0 right-0 top-0 border-t border-dashed border-neutral-200" />
              <div className="absolute left-0 right-0 border-t border-dashed border-neutral-200" style={{ top: "50%" }} />
              <div className="absolute left-0 right-0 bottom-0 border-t border-neutral-200" />
              <div className="absolute inset-0 flex items-end gap-1 px-1">
                {rows.map((r) => {
                  const trackH = CHART_H - 14; // reserve top space for the data label
                  const pct = Math.max(2, (r.qty_kg / axisMax) * 100);
                  return (
                    <div key={r.month} className="flex-1 flex flex-col items-center"
                         title={`${r.month}: ${r.qty_kg} kg / ${INR.format(r.revenue)}`}>
                      {/* Data label above the bar. */}
                      <div className="text-[8px] leading-tight text-neutral-500 mb-0.5">
                        {r.qty_kg > 0 ? r.qty_kg : ""}
                      </div>
                      {/* Bar track needs an explicit pixel height: it sits in
                          a `justify-end`-free column, so the bar's % height
                          resolves against this fixed value instead of an
                          indefinite one (which would collapse to 0). */}
                      <div className="w-full flex flex-col justify-end" style={{ height: trackH }}>
                        <div className="w-full bg-[#E5197F]/80 rounded-t"
                             style={{ height: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {rows.length > 0 && (
          <div className="flex gap-2">
            <div className="shrink-0" style={{ width: 24 }} />
            <div className="flex-1 flex gap-1 px-1">
              {rows.map((r) => {
                const [, mm] = r.month.split("-");
                return (
                  <div key={r.month} className="flex-1 text-center text-[9px] text-neutral-400">
                    {MONTH_SHORT[Number(mm) - 1] || r.month.slice(5)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SkuRow({ r, right }) {
  return (
    <div className="flex items-center gap-2 py-2 border-b last:border-b-0 border-neutral-100"
         data-testid={`rd-sku-row-${r.sku_id?.slice(0, 8)}`}>
      <Package className="h-4 w-4 text-neutral-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-neutral-900 truncate">{r.sku_name || r.sku_code}</div>
        {r.sku_name && <div className="text-[10px] text-neutral-400">{r.sku_code}</div>}
      </div>
      <div className="text-right shrink-0 text-xs">{right}</div>
    </div>
  );
}

function BlockShell({ icon: Icon, title, hint, testid, children }) {
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <Icon className="h-4 w-4" />{title}
        </div>
        {hint && (
          <div className="text-[11px] text-neutral-500 mt-0.5">{hint}</div>
        )}
        <div className="mt-2">{children}</div>
      </CardContent>
    </Card>
  );
}

function BestSellersBlock({ rows }) {
  return (
    <BlockShell icon={Flame} title="Best sellers (6-mo)"
                hint="What this outlet bought most in the last 6 months"
                testid="rd-best-sellers">
      {rows.length === 0 ? <Empty text="No purchases from this outlet in the last 6 months." /> : rows.map((r) => (
        <SkuRow key={r.sku_id} r={r}
                right={<><b>{r.qty_kg}kg</b><div className="text-neutral-400">{INR.format(r.revenue)}</div></>} />
      ))}
    </BlockShell>
  );
}

function FocusBlock({ rows, source }) {
  return (
    <BlockShell icon={Target}
                title="Focus SKU performance (6-mo)"
                hint={source === "admin_curated" ? "Products chosen by the office" : "Top-selling products of the last 90 days"}
                testid="rd-focus-perf">
      {rows.length === 0 ? <Empty /> : rows.map((r) => (
        <SkuRow key={r.sku_id} r={r}
                right={<b className={r.qty_kg > 0 ? "text-green-700" : "text-neutral-400"}>
                          {r.qty_kg}kg
                        </b>} />
      ))}
    </BlockShell>
  );
}

function MissingBlock({ rows, days }) {
  return (
    <BlockShell icon={PieChart}
                title={`Missing products (not bought for ${days || 60}+ days)`}
                hint={`Bought in the last 12 months, but not in the last ${days || 60} days`}
                testid="rd-missing">
      {rows.length === 0 ? <Empty text={`No product has stopped for more than ${days || 60} days.`} /> : rows.map((r) => (
        <SkuRow key={r.sku_id} r={r}
                right={<><div className="text-neutral-500">last {r.last_ordered_date}</div>
                          <div className="text-neutral-400">{r.historical_qty_kg}kg 12mo</div></>} />
      ))}
    </BlockShell>
  );
}

function NewSKUBlock({ rows, scope }) {
  return (
    <BlockShell icon={Sparkles}
                title="New products to offer"
                hint={`Products other outlets on the same ${scope === "zone" ? "zone" : "beat"} bought in the last 6 months that this outlet has never bought`}
                testid="rd-new-sku">
      {rows.length === 0 ? <Empty text="Nothing new to offer right now." /> : rows.map((r) => (
        <SkuRow key={r.sku_id} r={r}
                right={<><b>{r.peer_qty_kg}kg</b>
                          <div className="text-neutral-400">{r.peer_retailer_count} peers</div></>} />
      ))}
    </BlockShell>
  );
}

const SCHEME_TYPE_LABEL = {
  FREE_GOODS: "Free goods", QPS_QTY: "Quantity slab", qty_based: "Quantity slab",
  VALUE_DISCOUNT: "Value discount", PERCENT_DISCOUNT: "% discount", FLAT_DISCOUNT: "Flat discount",
};

function SchemesBlock({ schemes }) {
  const [eligibleOf, setEligibleOf] = useState(null);
  if (!schemes?.length) return null;
  return (
    <BlockShell icon={Boxes} title="Active schemes" testid="rd-schemes">
      {schemes.map((s) => (
        <div key={s.id}
             className="py-2 border-b last:border-b-0 border-neutral-100"
             data-testid={`rd-scheme-${s.id.slice(0, 8)}`}>
          <div className="text-sm font-semibold text-neutral-900">{s.name}</div>
          <div className="text-[11px] text-neutral-500">
            {SCHEME_TYPE_LABEL[s.scheme_type] || "Scheme"} · valid till {s.valid_to?.slice(0, 10)}
          </div>
          {s.offer_text && <div className="text-xs text-neutral-800 mt-1">{s.offer_text}</div>}
          {(s.eligible_skus || []).length > 0 && (
            <button type="button" onClick={() => setEligibleOf(s)} data-testid={`rd-scheme-eligible-${s.id.slice(0, 8)}`}
                    className="text-[11px] font-semibold text-[#6D2C91] mt-0.5">
              View all {s.eligible_skus.length} eligible products and kg ›
            </button>
          )}
          <div className="text-xs text-neutral-700 mt-1">
            {s.offer_text ? "Bought this month (these products): " : "Bought this month: "}
            <b>{Number(s.offer_text ? s.bought_kg : s.achievement_mtd.qty_kg || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg</b>
            {!s.offer_text && <> · {INR.format(s.achievement_mtd.value || 0)}</>}
          </div>
          {s.offer_text && (
            <div className={"text-xs mt-0.5 " + (s.earned_text ? "text-emerald-700 font-semibold" : "text-neutral-500")}>
              {s.earned_text ? `Free earned this month: ${s.earned_text}` : "No free reward earned yet this month."}
            </div>
          )}
          {s.balance_to_next_slab_qty != null && (
            <div className="text-[11px] text-amber-700 mt-0.5">
              {s.balance_to_next_slab_qty > 0
                ? <>Buy <b>{s.balance_to_next_slab_qty}</b> more to unlock next slab ({s.next_slab_qty}).</>
                : <>Next slab achieved.</>}
            </div>
          )}
        </div>
      ))}
      <Dialog open={!!eligibleOf} onOpenChange={(o) => !o && setEligibleOf(null)}>
        <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="rd-scheme-eligible-dialog">
          {eligibleOf && (
            <>
              <DialogHeader><DialogTitle>{eligibleOf.name}</DialogTitle></DialogHeader>
              <div className="text-xs text-neutral-600">
                Buy the kg shown on any of these products this month and get {eligibleOf.free_item} free for every full lot.
              </div>
              <div className="divide-y divide-neutral-100">
                {eligibleOf.eligible_skus.map((e) => (
                  <div key={e.sku_id} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="text-neutral-900">{e.name}</div>
                      <div className="text-[11px] text-neutral-500">Every {e.need_kg} kg → 1 free</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={e.bought_kg > 0 ? "font-semibold text-neutral-900" : "text-neutral-400"}>
                        {e.bought_kg} kg bought
                      </div>
                      {e.earned > 0 && <div className="text-[11px] text-emerald-700 font-semibold">{e.earned} free earned</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </BlockShell>
  );
}

function Empty({ text }) {
  return <div className="text-xs text-neutral-400 py-3 text-center">{text || "Nothing to show."}</div>;
}

// -------------------------------------------------------------- History tab
// Past transactions — one row per order: date, status, SKUs bought + kg.
// Backed by GET /retailers/{id}/order-history (backend/routes_phase29_3.py).
const STATUS_LABEL = {
  submitted: "Submitted", confirmed: "Confirmed", dispatched: "Dispatched",
  delivered: "Delivered", approved: "Approved", booked: "Booked",
  rejected: "Rejected", cancelled: "Cancelled",
};

function HistoryPanel({ retailerId }) {
  const [orders, setOrders] = useState(null);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/retailers/${retailerId}/order-history`,
                                       { params: { months: 12 } });
      setOrders(data.orders || []);
      setReturns(data.returns || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [retailerId]);

  useEffect(() => { load(); }, [load]);

  if (loading && orders === null) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  // Orders and returns in one list, newest first.
  const rows = [
    ...(orders || []).map((o) => ({ kind: "order", date: o.order_date || "", o })),
    ...returns.map((r) => ({ kind: "return", date: r.return_date || "", r })),
  ].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const netKg = (orders || []).filter((o) => !["rejected", "cancelled"].includes(o.status))
    .reduce((a, o) => a + (o.total_qty_kg || 0), 0) - returns.reduce((a, r) => a + (r.total_qty_kg || 0), 0);

  return (
    <div className="space-y-2" data-testid="rd-history-panel">
      {returns.length > 0 && (
        <div className="text-[11px] text-neutral-600 px-1" data-testid="rd-history-net">
          Net kg in this period (orders − returns): <b>{netKg.toFixed(2)} kg</b>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-xs text-neutral-400 py-4 text-center">
          No past orders in the last 12 months.
        </div>
      ) : rows.map((row) => row.kind === "return" ? (
        <Card key={row.r.return_id} className="border-rose-200 bg-rose-50/40 rounded-2xl"
              data-testid={`rd-history-return-${row.r.return_id.slice(0, 8)}`}>
          <CardContent className="p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-neutral-900">{row.r.return_date}</div>
              <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold">Return</div>
            </div>
            <div className="text-[11px] text-rose-700">
              RET-{row.r.return_id.slice(0, 8).toUpperCase()} · −{row.r.total_qty_kg} kg
            </div>
            <div className="space-y-1 pt-1">
              {row.r.lines.map((l, i) => (
                <div key={i} className="text-xs flex items-center justify-between">
                  <span className="text-neutral-700 truncate pr-2">
                    {l.sku_name || l.sku_code}{l.reason ? ` · ${l.reason}` : ""}
                  </span>
                  <span className="text-rose-700 shrink-0">−{l.qty_kg} kg</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : [row.o].map((o) => (
        <Card key={o.order_id} className="border-neutral-200 rounded-2xl"
              data-testid={`rd-history-row-${o.order_id.slice(0, 8)}`}>
          <CardContent className="p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-neutral-900">{o.order_date}</div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500">
                {STATUS_LABEL[o.status] || o.status}
              </div>
            </div>
            <div className="text-[11px] text-neutral-400">
              {o.order_no} · {o.total_qty_kg} kg total · {INR.format(o.net_amount)}
            </div>
            <div className="space-y-1 pt-1">
              {o.lines.map((l, i) => (
                <div key={i} className="text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-700 truncate pr-2">
                      {l.sku_name || l.sku_code}
                    </span>
                    <span className="text-neutral-500 shrink-0">{l.qty_kg} kg</span>
                  </div>
                  {(l.schemes || []).map((sc, k) => (
                    <div key={k} className="text-[10px] text-[#E5197F] font-semibold">
                      🎁 {sc.name}
                      {sc.benefit && ` — ${sc.benefit}`}
                      {sc.discount > 0 && ` · −${INR.format(sc.discount)}`}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )))}
    </div>
  );
}

// ---------------------------------------------------------- Current Stock tab
function StockPanel({ retailerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [entrySku, setEntrySku] = useState(null);   // {id, name, code}
  const entrySkuId = entrySku?.id || "";
  const [entryQty, setEntryQty] = useState("");
  const [entryNotes, setEntryNotes] = useState("");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/retailers/${retailerId}/current-stock`);
      setData(r);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [retailerId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!entrySkuId || !entryQty) return toast.error("Pick a product and enter the stock in kg.");
    setBusy(true);
    try {
      await api.post(`/retailers/${retailerId}/current-stock`, {
        sku_id: entrySkuId, qty: Number(entryQty), uom: "kg",
        notes: entryNotes || null,
      });
      toast.success("Stock recorded");
      setEntrySku(null); setEntryQty(""); setEntryNotes("");
      setSearch(""); setSearchResults([]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) return setSearchResults([]);
    try {
      const { data } = await api.get("/skus", { params: { q, page_size: 10 } });
      setSearchResults(data.items || []);
    } catch (e) { /* silent */ }
  };

  const previously = useMemo(() => data?.previously_purchased || [], [data]);

  return (
    <div className="space-y-3" data-testid="rd-stock-panel">
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-2">
          <div className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <PlusCircle className="h-4 w-4" /> Quick stock entry
          </div>
          {entrySkuId ? (
            <div className="text-xs text-neutral-700 bg-neutral-50 rounded-lg px-2 py-1 flex items-center justify-between"
                 data-testid="rd-stock-entry-sku">
              <span className="truncate">{entrySku?.name || entrySku?.code}</span>
              <button className="text-red-600 text-xs shrink-0 ml-2"
                onClick={() => { setEntrySku(null); }}>Clear</button>
            </div>
          ) : (
            <>
              <Input placeholder="Search product by name or code…"
                     value={search}
                     onChange={(e) => runSearch(e.target.value)}
                     data-testid="rd-stock-search" />
              {searchResults.length > 0 && (
                <div className="rounded-lg border border-neutral-200 max-h-40 overflow-y-auto">
                  {searchResults.map((s) => (
                    <button key={s.id}
                            onClick={() => { setEntrySku({ id: s.id, name: s.name, code: s.sku_code }); setSearchResults([]); }}
                            data-testid={`rd-stock-search-${s.id.slice(0, 8)}`}
                            className="w-full text-left text-xs px-2 py-1.5 hover:bg-neutral-50 border-b last:border-b-0">
                      <b>{s.sku_code}</b> · {s.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          <Input type="number" inputMode="decimal" step="0.01" min="0" placeholder="Stock at outlet now (kg)"
                 value={entryQty}
                 onChange={(e) => setEntryQty(e.target.value)}
                 data-testid="rd-stock-qty" />
          <Input placeholder="Notes (optional)"
                 value={entryNotes}
                 onChange={(e) => setEntryNotes(e.target.value)}
                 data-testid="rd-stock-notes" />
          <Button onClick={submit} disabled={busy || !entrySkuId || !entryQty}
                  className="w-full h-10 rounded-xl tulsi-gradient text-white"
                  data-testid="rd-stock-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Record stock
          </Button>
        </CardContent>
      </Card>

      <BlockShell icon={Package} title="Previously purchased"
                  testid="rd-stock-purchased">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto my-4 text-neutral-400" />
         : previously.length === 0 ? <Empty />
         : previously.map((r) => (
            <div key={r.sku_id}
                 className="flex items-center gap-2 py-2 border-b last:border-b-0 border-neutral-100"
                 data-testid={`rd-stock-prev-${r.sku_id?.slice(0, 8)}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-neutral-900 truncate">{r.sku_name || r.sku_code}</div>
                <div className="text-[11px] text-neutral-500">
                  Last order {r.last_ordered_date}
                  {r.last_stock_entered_at
                    ? ` · Last stock ${new Date(r.last_stock_entered_at).toLocaleDateString()}: ${r.last_stock_qty} ${r.last_stock_uom}`
                    : ""}
                </div>
              </div>
              <Button size="sm" variant="outline"
                onClick={() => setEntrySku({ id: r.sku_id, name: r.sku_name, code: r.sku_code })}
                data-testid={`rd-stock-use-${r.sku_id?.slice(0, 8)}`}>
                Use
              </Button>
            </div>
          ))}
      </BlockShell>

      <BlockShell icon={Boxes}
                  title={`Recent stock entries (${data?.history_count || 0})`}
                  testid="rd-stock-history">
        {(data?.history || []).length === 0 ? <Empty /> : data.history.slice(0, 20).map((h) => (
          <div key={h.id}
               className="flex items-center justify-between py-1.5 text-xs border-b last:border-b-0 border-neutral-100"
               data-testid={`rd-stock-hist-${h.id?.slice(0, 8)}`}>
            <span className="truncate flex-1">{h.sku_name_snapshot || h.sku_code_snapshot}</span>
            <span className="text-neutral-500 shrink-0 ml-2">
              {h.qty} {String(h.uom || "").toLowerCase() === "kg" ? "kg" : h.uom} · {new Date(h.entered_at).toLocaleDateString()}
            </span>
          </div>
        ))}
      </BlockShell>
    </div>
  );
}
